import {
  TransactionFilters,
  PaginationState,
  SearchState,
  DEFAULT_FILTERS,
  DEFAULT_PAGINATION,
  VALID_STATUSES,
  DATE_FORMAT_REGEX,
  PAGE_MIN,
  PER_PAGE_MIN,
  PER_PAGE_MAX,
} from './types';

/**
 * Serializa SearchState a URLSearchParams, omitiendo valores iguales al default.
 * Solo incluye parámetros cuyo valor difiera del valor por defecto.
 *
 * Validates: Requirements 3.1, 3.2, 3.6
 */
export function serializeSearchState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams();

  // Serializar filtros — solo si difieren del default
  const filterKeys = Object.keys(DEFAULT_FILTERS) as (keyof TransactionFilters)[];
  for (const key of filterKeys) {
    const value = state.filters[key];
    const defaultValue = DEFAULT_FILTERS[key];
    if (value !== defaultValue) {
      params.set(key, value);
    }
  }

  // Serializar paginación — solo si difiere del default
  if (state.pagination.page !== DEFAULT_PAGINATION.page) {
    params.set('page', String(state.pagination.page));
  }
  if (state.pagination.per_page !== DEFAULT_PAGINATION.per_page) {
    params.set('per_page', String(state.pagination.per_page));
  }

  return params;
}

/**
 * Deserializa URLSearchParams a SearchState, validando cada parámetro.
 * Los parámetros inválidos se ignoran y se aplica el valor por defecto.
 * Los parámetros válidos se preservan incluso si otros son inválidos.
 *
 * Validates: Requirements 1.5, 3.3, 3.5, 5.1
 */
export function deserializeSearchState(params: URLSearchParams): SearchState {
  const filters: TransactionFilters = { ...DEFAULT_FILTERS };
  const pagination: PaginationState = { ...DEFAULT_PAGINATION };

  // Deserializar filtros
  const filterKeys = Object.keys(DEFAULT_FILTERS) as (keyof TransactionFilters)[];
  for (const key of filterKeys) {
    const raw = params.get(key);
    if (raw !== null && raw !== '') {
      const validated = validateFilterParam(key, raw);
      if (validated !== undefined) {
        filters[key] = validated;
      }
    }
  }

  // Deserializar paginación
  const pageRaw = params.get('page');
  if (pageRaw !== null && pageRaw !== '') {
    const validatedPage = validatePaginationParam('page', pageRaw);
    if (validatedPage !== undefined) {
      pagination.page = validatedPage;
    }
  }

  const perPageRaw = params.get('per_page');
  if (perPageRaw !== null && perPageRaw !== '') {
    const validatedPerPage = validatePaginationParam('per_page', perPageRaw);
    if (validatedPerPage !== undefined) {
      pagination.per_page = validatedPerPage;
    }
  }

  return { filters, pagination };
}

/**
 * Valida un valor individual de filtro según las reglas de negocio.
 * Retorna el valor validado si es válido, undefined si es inválido.
 *
 * Reglas:
 * - status: solo valores COMPLETED, PENDING, DECLINED, CANCELLED, REFUNDED (CSV)
 * - payment_method_id: numérico o CSV de numéricos
 * - country: código ISO 3166-1 alpha-2, 2 letras mayúsculas (CSV)
 * - currency: código ISO 4217, 3 letras mayúsculas
 * - from, to: formato ISO 8601 date (YYYY-MM-DD)
 * - search_by: string libre (sin validación especial)
 *
 * Validates: Requirements 3.5, 1.5
 */
export function validateFilterParam(
  key: string,
  value: string
): string | undefined {
  switch (key) {
    case 'status': {
      const statuses = value.split(',');
      const allValid = statuses.every((s) =>
        (VALID_STATUSES as readonly string[]).includes(s)
      );
      return allValid ? value : undefined;
    }

    case 'payment_method_id': {
      const ids = value.split(',');
      const allNumeric = ids.every((id) => /^\d+$/.test(id));
      return allNumeric ? value : undefined;
    }

    case 'country': {
      const countries = value.split(',');
      const allValid = countries.every((c) => /^[A-Z]{2}$/.test(c));
      return allValid ? value : undefined;
    }

    case 'currency': {
      return /^[A-Z]{3}$/.test(value) ? value : undefined;
    }

    case 'from':
    case 'to': {
      return DATE_FORMAT_REGEX.test(value) ? value : undefined;
    }

    case 'search_by': {
      // String libre, sin validación especial
      return value;
    }

    case 'transaction_id':
    case 'external_id':
    case 'client_name':
    case 'client_email':
    case 'tx_email':
    case 'document':
    case 'total_amount': {
      // Search fields — string libre
      return value;
    }

    default:
      return undefined;
  }
}

/**
 * Valida un valor de paginación.
 * Retorna el número parseado si es válido, undefined si es inválido.
 *
 * Reglas:
 * - page: entero >= 1
 * - per_page: entero entre 1 y 100
 *
 * Validates: Requirements 3.5, 1.5
 */
export function validatePaginationParam(
  key: 'page' | 'per_page',
  value: string
): number | undefined {
  // Debe ser un string numérico entero (sin decimales, sin espacios)
  if (!/^\d+$/.test(value)) {
    return undefined;
  }

  const num = parseInt(value, 10);

  if (key === 'page') {
    return num >= PAGE_MIN ? num : undefined;
  }

  if (key === 'per_page') {
    return num >= PER_PAGE_MIN && num <= PER_PAGE_MAX ? num : undefined;
  }

  return undefined;
}
